import { SyntheticEvent, useRef, useState } from "react";
import { useAppDispatch } from "../../../app";
import { addBlurredEntity, changeEditing } from "../../../app/store/appSlice";
import { isEmpty } from "../../utilities/value";
import { GroupBlockEntityType } from "../../../configMap";
import useAnnotations from "../../../annotation/hooks/useAnnotations";
import useKeyEvent from "../../../configMap/hooks/useKeyEvent";
import { Keys } from "../../constants";

type StepInfo = {
  offset: number | string;
  type: "up" | "down";
};

interface UseEditEntityNumberProps {
  entityType: GroupBlockEntityType;
  documentId: string;
  annotationId: string;
  defaultValue: number;
  groupBlockIndex: number;
  isTableMode: boolean;
}

const useEditEntityNumber = ({
  entityType,
  documentId,
  annotationId,
  defaultValue,
  groupBlockIndex,
  isTableMode,
}: UseEditEntityNumberProps) => {
  const dispatch = useAppDispatch();

  const isCancelled = useRef(false);
  const [value, setValue] = useState<number | null>(defaultValue);

  const { updateAnnotation } = useAnnotations({
    documentId,
    groupBlockIndex,
  });

  const handleKeyEvent = useKeyEvent(Keys.ESCAPE, () =>
    dispatch(changeEditing(null)),
  );

  const handleStep = (updateValue: number, info: StepInfo): void => {
    const { type } = info;

    switch (type) {
      case "up":
        setValue((oldValue: number | null) => {
          if (oldValue) {
            return oldValue + updateValue;
          }
          return oldValue;
        });
        break;
      case "down":
        setValue((oldValue: number | null) => {
          if (oldValue) {
            return oldValue - updateValue;
          }
          return oldValue;
        });
        break;
    }
  };

  const handleEnter = (event: SyntheticEvent) => {
    event.stopPropagation();
    dispatch(changeEditing(null));
    if (
      !isEmpty(value) &&
      !Number.isNaN(Number(value)) &&
      !isCancelled.current
    ) {
      updateAnnotation(
        annotationId,
        value!.toString(),
        entityType,
        true,
        isTableMode ? true : entityType.multipleGroupBlocks,
      );
    }
  };

  const onBlur = (event: SyntheticEvent) => {
    handleEnter(event);
    dispatch(
      addBlurredEntity(`${entityType.entityType.id}-${groupBlockIndex}`),
    );
  };

  const handleCancel = (event: SyntheticEvent) => {
    event.stopPropagation();
    isCancelled.current = true;
    dispatch(changeEditing(null));
  };

  return {
    value,
    setValue,
    handleStep,
    handleEnter,
    onBlur,
    handleCancel,
    handleKeyEvent,
  };
};

export default useEditEntityNumber;
